import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Loader2, Check } from 'lucide-react';
import { API } from '@/App';
import PasswordInput from '@/components/PasswordInput';
import { passwordComplexityErrors } from '@/utils/passwordComplexity';

const AdminResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // No token at all → show error immediately
  useEffect(() => {
    if (!token) {
      setError('Reset link is missing the token. Please request a new password reset email.');
    }
  }, [token]);

  const liveErrs = passwordComplexityErrors(newPwd);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Reset link is missing the token. Please request a new password reset email.');
      return;
    }
    if (liveErrs.length > 0) {
      setError('New password missing: ' + liveErrs.join(', '));
      return;
    }
    if (newPwd !== confirmPwd) {
      setError('New password and confirmation do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/admin-password-reset-confirm`, {
        token,
        new_password: newPwd,
      });
      setDone(true);
    } catch (err) {
      const status = err.response && err.response.status;
      if (status === 401) {
        setError('This reset link is invalid, expired, or has already been used. Please request a new one.');
      } else if (status === 400) {
        setError(err.response.data?.detail || 'Password reset failed.');
      } else {
        setError('Password reset failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-md w-full mx-4">
          <div className="bg-gray-900 border-2 border-[#FFDD59] rounded-2xl p-8 shadow-2xl text-center">
            <Check className="mx-auto mb-3" size={48} color="#FFDD59" />
            <h2 className="text-2xl font-bold text-white mb-2">Password reset successful</h2>
            <p className="text-sm text-gray-400 mb-6">
              You can now log in with your new password. Your 2FA setup is preserved.
            </p>
            <button
              type="button"
              onClick={() => navigate('/adminboraorkun')}
              className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition"
            >
              Go to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center relative py-12">
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
      </div>
      <div className="relative z-10 max-w-md w-full mx-4">
        <div className="bg-gray-900 border-2 border-[#FFDD59] rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-6">
            <Lock className="mx-auto mb-3" size={48} color="#FFDD59" />
            <h2 className="text-2xl font-bold text-white mb-2">Reset Your Password</h2>
            <p className="text-sm text-gray-400">
              Choose a new password. Your 2FA enrollment remains unchanged.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                New password
              </label>
              <PasswordInput
                id="reset-new-password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                autoComplete="new-password"
              />
              {newPwd.length > 0 && liveErrs.length > 0 && (
                <ul className="mt-2 text-xs text-yellow-300 list-disc list-inside space-y-1">
                  {liveErrs.map((m) => <li key={m}>{m}</li>)}
                </ul>
              )}
              {newPwd.length > 0 && liveErrs.length === 0 && (
                <p className="mt-2 text-xs text-green-400">✓ Meets complexity requirements</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Confirm new password
              </label>
              <PasswordInput
                id="reset-confirm-password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                autoComplete="new-password"
              />
              {confirmPwd.length > 0 && confirmPwd !== newPwd && (
                <p className="mt-2 text-xs text-red-400">Passwords do not match</p>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-900/50 border border-red-500 text-red-200 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !token}
              className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={18} /> Resetting...
                </>
              ) : 'Reset password'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/adminboraorkun')}
              className="w-full py-2 text-sm text-gray-400 hover:text-white transition"
            >
              Back to login
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminResetPasswordPage;
